/**
 * Site Control — centralised copy for the per-site enterprise plan/tier.
 *
 * Publicly LAUNCHED (GA). The `site_control` feature flag is now a KILL-SWITCH
 * (isSiteControlLive) — the surface is live unless an operator explicitly
 * disables it; it is NOT a pre-launch gate. STRIPE_PRICE_SITE_CONTROL_MONTHLY
 * only affects provisioning after a sales-led sale, not marketing visibility.
 *
 * Marketing surfaces import from here so the strings stay consistent.
 * Commercial brand = "Site Control"; the in-app feature/mode stays "Site
 * Access" (the #557 product) — like the Business plan vs the "site portal"
 * feature.
 */

export const SITE_CONTROL_NAME = "Site Control" as const;

export const SITE_CONTROL_PRICE_PER_SITE_GBP = 675 as const;
export const SITE_CONTROL_SETUP_GBP = 5000 as const;
export const SITE_CONTROL_PRICE_DISPLAY = "£675" as const;
export const SITE_CONTROL_PRICE_PERIOD = "/site per month" as const;
export const SITE_CONTROL_SETUP_DISPLAY = "£5,000" as const;

export const SITE_CONTROL_PRICE_HEADLINE =
  "From £675/site per month, plus £5,000 setup" as const;

export const SITE_CONTROL_TAGLINE =
  "Per-site contractor access and CDM compliance for principal contractors running live sites." as const;

// Pricing-page section-break copy — frames the Site Control card as a distinct
// enterprise product class above the self-serve grid, and the hero signpost
// that points down to it. Kept here so the strings stay in one place (no
// duplicated literals in components).
export const SITE_CONTROL_SECTION_EYEBROW = "Enterprise tier" as const;
export const SITE_CONTROL_SECTION_HEADING =
  "For multi-site principal contractors" as const;
export const SITE_CONTROL_HERO_SIGNPOST =
  "Running multiple sites with contractors? See Site Control" as const;

export const SITE_CONTROL_CTA = "Book a walkthrough" as const;

// Compare-page CTA copy — consumed by lib/compare/cta.ts SITE_CONTROL_CTA_DESCRIPTOR
// so all Site Control CTA wording lives in one place.
export const SITE_CONTROL_CTA_SECONDARY = "See it in action" as const;
// Review MKT-9: no walkthrough video exists — don't promise one.
export const SITE_CONTROL_CTA_WATCH = "See Site Control in action" as const;
export const SITE_CONTROL_CTA_PRIMARY_BODY =
  "Per-site contractor access for principal contractors — talk to us about your live sites." as const;
export const SITE_CONTROL_CTA_SECONDARY_BODY =
  "See how Site Control runs QR sign-in, CSCS, induction gating, attendance and RFIs on a live site." as const;

/** Volume tiers (per active site / month). Surfaced on the sales conversation. */
export const SITE_CONTROL_VOLUME_TIERS = [
  { sites: "1–3 sites", pricePerSiteGbp: 675, display: "£675/site" },
  { sites: "4–9 sites", pricePerSiteGbp: 595, display: "£595/site" },
  { sites: "10+ sites", pricePerSiteGbp: 495, display: "£495/site" },
] as const;

export const SITE_CONTROL_FEATURES = [
  "Per active site, with unlimited workers",
  "Permanent entrance QR: guest sign-in and contractor self-join",
  "CSCS credential capture, review and expiry alerts",
  "Digital induction gating before check-in",
  "Live people-on-site, attendance and audit-grade exports",
  "RFIs and version-specific document-review evidence",
  "Handover sign-off: per-plot, per-trade sign-off with a certified PDF",
  "Multi-contractor-company data isolation",
  "Everything in Business and Pro, including UK CDM document generation",
] as const;
